import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import moment from "moment-timezone";
import { Table, Popconfirm, Form, Button, Select, Space, Modal, Typography, Empty } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import styled, { css } from "styled-components";

import { Statuses, getFirstStep, Keys } from "../constant/status";
import { DateRangeFilter } from "./filter/DateRangeFilter";
import {
  THIS_YEAR,
  NEXT_12_MONTHS,
  NEXT_CALENDAR_YEAR,
  PREVIOUS_YEAR,
  ALL,
  ADMIN_DATE_FILTER_ID,
  ADMIN_DATE_FILTER_THIS_YEAR_ID,
  ADMIN_DATE_FILTER_NEXT_12_MONTHS_ID,
  ADMIN_DATE_FILTER_NEXT_CALENDAR_YEAR_ID,
  ADMIN_DATE_FILTER_PREVIOUS_YEAR_ID,
  ADMIN_DATE_FILTER_ALL_CYCLES_ID,
  ADMIN_DATE_FILTER_CUSTOM_ID
} from "../constant/filters";
import { UnitFormItem } from "../component/form/UnitFormItem";
import { OrderTypeFormItem } from "./form/OrderTypeFormItem";
import { DosageFormItem } from "./form/DosageFormItem";
import { OrderDateFormItem } from "./form/OrderDateFormItem";
import { AdminDateFormItem } from "./form/AdminDateFormItem";
import { EstimatedDateFormItem } from "./form/EstimatedDateFormItem";
import { BillVoidedFormItem } from "./form/BillVoidedFormItem";
import { StatusFormItem } from "./form/StatusFormItem";
import { OrderType } from "../constant/orderType";
import ACTIONS from "../store/action";
import {
  selectIsFetching,
  selectKeepEditingCycleMode,
  selectEnableFlash,
  selectPatientNotes,
  selectPatientAttachments,
  selectCycleAdminDateFilter,
  selectPatientCycles,
  selectIsAddingPatientCycle
} from "../store/selector";
import { ApiNames } from "../api/api";
import EllipseIcon from "../assets/svg/EllipseIcon";
import { getNewCycle } from "../utils/cycles";
import EllipseEmptyIcon from "../assets/svg/EllipseEmptyIcon";
import { SORT_DIRECTIONS, ASCEND } from "../constant/sort";
import { ArrivedDateFormItem } from "./form/ArrivedDateFormItem";
import { DATE_FORMAT_2 } from "../utils/date";
import { NumericalInput } from "./form/NumericalInput";
import { SingleCycleForm } from "./SingleCycleForm";
import { LOT_NUMBER_MAX, REFILL_MAX } from "../constant/cycles";
import { LotNumberFormItem } from "./form/LotNumberFormItem";
import { BillVoidedDateFormItem } from "./form/BillVoidedDateFormItem";
import { PRIMARY_50, BLUE, GRAY_50, GRAY_100, BLACK, GRAY_200, GRAY_400 } from "../constant/colors";
import { ReactComponent as EditPencilIcon } from "../assets/svg/edit-pencil-icon.svg";
import { TableStyle } from "./SavedList/TableStyle";
import { PatientViewTableStyle } from "./SavedList/PatientViewTableStyle";
import { ReactComponent as WarningIcon } from "../assets/svg/warning-icon-popup.svg";
import { ReactComponent as DeleteIcon } from "../assets/svg/delete-new-icon.svg";
import { ModalComponent } from "../component/customComponent/ModalComponent";
import { AutoEllipsisTooltipColumnValue } from "./customComponent/CommonColumns";
import { fontWeights, sizes } from "../constant/styles";

const { Option } = Select;
const { Text } = Typography;
const INVALID_DATE = "-";

const ReachableContext = React.createContext();

const StyledTable = styled(Table)`
  ${TableStyle}
  ${PatientViewTableStyle}

  .ant-table {
    margin: 0;
    max-height: 100vh;
  }

  .ant-table-body {
    overflow-y: auto !important;
    overflow-x: auto !important;
  }

  .ant-table::-webkit-scrollbar:vertical {
    display: none;
  }

  .ant-table-thead > tr> .ant-table-cell-fix-left {
    background: ${GRAY_100} !important;
  }

  .ant-table-thead > tr> .ant-table-cell-fix-left.ant-table-column-has-sorters {
    &:hover {
      background: #f0f0f0 !important;
    }
  }

  .ant-table-empty {
    .ant-empty{
      min-height: 30vh;
      max-height: 30vh;
      height: auto;
      display: flex;
      justify-content: center;
      flex-direction: column;

      @media (max-height: 778px) {
        min-height: 38vh;
        max-height: 42vh;
      }

      @media (max-height: 768px) {
        min-height: 41vh;
        max-height: 45vh;
      }
    }
  }

  .ant-form-item-explain-error{
    white-space: break-spaces;
  }

  @keyframes flash {
    0% {
      background-color: ${PRIMARY_50};
    }
    50% {
      background-color: ${BLUE};
    }
    100% {
      background-color: ${PRIMARY_50};
    }
  }
  .ant-table-tbody > tr.highlight-row.ant-table-row:hover > td, .ant-table-tbody > tr.highlight-row > td.ant-table-cell-row-hover {
     ${({ enableFlash }) =>
       enableFlash
         ? css`
             background-color: ${BLUE} !important;
           `
         : css`
             background-color: ${PRIMARY_50};
           `}
    }

    tbody > tr.highlight-row td.ant-table-column-sort{
      ${({ enableFlash }) =>
        enableFlash
          ? css`
              animation: none !important;
              animation: flash 2s ease-in-out 1 !important;
            `
          : css`
              background-color: ${PRIMARY_50} !important;
            `}
    }

  tbody > tr.highlight-row {
      ${({ enableFlash }) =>
        enableFlash &&
        css`
          animation: flash 2s ease-in-out 1;
        `}
    background-color: ${PRIMARY_50};
    &:hover {
         ${({ enableFlash }) =>
           enableFlash &&
           css`
             background-color: ${BLUE} !important;
           `}
      }

    .ant-table-cell-fix-left,
    .ant-table-cell-fix-right {
      ${({ enableFlash }) =>
        enableFlash &&
        css`
          animation: flash 2s ease-in-out 1;
        `}
      background-color: ${PRIMARY_50};
      &:hover {
         ${({ enableFlash }) =>
           enableFlash &&
           css`
             background-color: ${BLUE};
           `}
      }
    }
`;

const EditButtonStyle = styled(Button)`
  justify-content: center;
  align-items: center;
  display: flex;
  ${({ disabled }) =>
    disabled &&
    css`
      & > svg > path {
        stroke: rgba(0, 0, 0, 0.25) !important;
      }
    `}
`;

const StyledDateRangeFilter = styled.div`
  .ant-select-suffix {
    color: ${BLACK} !important;
  }

  .anticon-calendar {
    color: ${BLACK} !important;
  }

  .ant-select-selection-item {
    color: ${BLACK} !important;
    font-size: ${sizes.medium} !important;
    font-weight: ${fontWeights.regular} !important;
  }
`;

const StyledAdminDateLabel = styled(Text)`
  color: ${BLACK};
  font-size: ${sizes.small};
  font-weight: ${fontWeights.bold};
`;

const StyledEmptyDescription = styled.div`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  color: ${BLACK};
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

const PatientCyclesTabComponent = ({
  t,
  cycles,
  patient,
  isFetchingChangeCycle,
  isFetchingCycleDelete,
  setEdited,
  isActive,
  changeCurrentOrderAct,
  deletePatientCycleAct,
  addPatientCycleAct,
  bulkChangeAct,
  edited
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [modal, contextHolder] = Modal.useModal();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCycleStatusManuallyChanged, setCycleStatusManuallyChanged] = useState(false);
  const [editingAll, setEditingAll] = useState(false);
  const [isLoadingUpdateCycleInfo, setIsLoadingUpdateCycleInfo] = useState(false);
  const [tableDate, setTableData] = useState([]);
  const cycleTableRef = useRef(null);
  const [isFlashEnabled, seIsFlashEnabled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const isFetchingUpdateCycleInfo = useSelector(selectIsFetching([ApiNames.BulkChange]));
  const isFetchingPharmacyPatients = useSelector(selectIsFetching([ApiNames.PharmacyPatients]));
  const keepEditingCycleMode = useSelector(selectKeepEditingCycleMode);
  const enableFlash = useSelector(selectEnableFlash);
  const isBulkChangeFetching = useSelector(selectIsFetching([ApiNames.BulkChange]));
  const isFetchingCycles = useSelector(selectIsFetching([ApiNames.GetCycles]));
  const patientNotes = useSelector(selectPatientNotes);
  const patientAttachments = useSelector(selectPatientAttachments);
  const adminDateFilter = useSelector(selectCycleAdminDateFilter);
  const allPatientCycles = useSelector(selectPatientCycles);
  const isAddingPatientCycle = useSelector(selectIsAddingPatientCycle);

  const setKeepEditingCycleAct = (mode) => dispatch(ACTIONS.setKeepEditingCycleMode(mode));
  const setEnableCycleFlashAct = (enableFlash) => dispatch(ACTIONS.setEnableCycleFlash(enableFlash));

  const setIsAddingPatientCycle = (isAddingPatientCycle) =>
    dispatch(ACTIONS.setIsAddingPatientCycle(isAddingPatientCycle));

  useEffect(() => {
    const timer = setTimeout(() => {
      seIsFlashEnabled(false);
      setEnableCycleFlashAct(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      seIsFlashEnabled(false);
      setEnableCycleFlashAct(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (cycleTableRef.current && patient.currentOrder?.orderIndex !== undefined) {
      const rowElement = cycleTableRef.current.querySelector(`tr[data-row-key="${patient.currentOrder.orderIndex}"]`);
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [cycleTableRef.current]);

  useEffect(() => {
    seIsFlashEnabled(enableFlash);
  }, [enableFlash]);

  useEffect(() => {
    if (!isFetchingUpdateCycleInfo && !keepEditingCycleMode) {
      setIsLoadingUpdateCycleInfo(false);
      setEdited(false);
      setEditingKey("");
      setEditingAll(false);
    }
  }, [isFetchingUpdateCycleInfo, keepEditingCycleMode]);

  useEffect(() => {
    setTableData(
      cycles?.map((item, index) => ({
        ...item,
        key: index,
        orderDate: item.orderDate ? moment(item.orderDate) : null,
        administrationDate: item.administrationDate ? moment(item.administrationDate) : null,
        estimatedDelivery: item.estimatedDelivery && moment(item.estimatedDelivery),
        arriveDate: item.arriveDate && moment(item.arriveDate),
        status: item.status.includes("-free") ? item.status.replace("-free", "") : item.status,
        billVoidedDate: item.billVoidedDate && moment(item.billVoidedDate)
      }))
    );
  }, [cycles]);

  const isEditing = (record) => record.key === editingKey;
  const recordForEditing = form.getFieldsValue(true);
  const isEditingActive = editingAll || editingKey !== "";
  const isCycleWithNoteOrAttachments = (record) => {
    return (
      !!patientNotes.find((note) => note.cycle === record.orderIndex + 1 && note.pharmacyPatient === patient.id) ||
      !!patientAttachments.find((attch) => attch.cycle?.id === record.id)
    );
  };

  const EditableCell = ({ editing, inputNode, changeCurrentCycle, children, ...props }) => (
    <td
      {...props}
      onClick={() => {
        if (
          !changeCurrentCycle ||
          editing ||
          props.dataIndex === undefined ||
          props.record.id === (patient.pharmacyPatientCurrentOrder || patient.currentOrder.id)
        )
          return null;

        setIsModalOpen(true);
        modal.confirm({
          centered: true,
          className: "formPopupsModal",
          icon: <WarningIcon />,
          okText: t("yes_sure"),
          cancelText: t("back"),
          title: t("patient.cycles.change_cycle_modal_title"),
          content: t("patient.cycles.change_cycle_modal_content"),
          onOk() {
            return new Promise((resolve) => {
              onChangeCurrCycle(props.record);
              const interval = setInterval(() => {
                if (!isBulkChangeFetching) {
                  clearInterval(interval);
                  setIsModalOpen(false);
                  resolve();
                }
              }, 1000);
              setTimeout(() => {
                clearInterval(interval);
                setIsModalOpen(false);
                resolve();
              }, 10000);
            });
          }
        });
      }}
    >
      {editing ? inputNode : children}
    </td>
  );

  const updateFormData = () => {
    const formValues = tableDate.reduce((row, data, index) => {
      row[index] = prepareCycleDataForForm(data);
      return row;
    }, {});
    form.setFieldsValue(formValues);
  };

  const edit = (record) => {
    setEdited(true);
    setKeepEditingCycleAct(true);
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
  };

  const setTypeAll = (e) => {
    const pharmacyPatient = {
      id: cycles[0].pharmacyPatient,
      pharmacyPatient: cycles[0].pharmacyPatient,
      isFreeDrug: e,
      status: getFirstStep(e)
    };

    const newCycles = cycles.map((cycle) => ({
      ...cycle,
      isFreeDrug: e,
      status: getFirstStep(e)
    }));
    bulkChangeAct(pharmacyPatient, newCycles);
  };

  const isDataContainsReplacemnetDrug = () => tableDate?.some((record) => record.isFreeDrug === false);

  const cancel = () => {
    setEdited(false);
    setEditingAll(false);
    setEditingKey("");
    setCycleStatusManuallyChanged(false);
    setKeepEditingCycleAct(false);
  };

  const prepareCycleDataForForm = (cycle) => {
    const formatDate = (date) => (date ? moment(date) : null);

    return {
      accountNumber: cycle.accountNumber,
      administrationDate: formatDate(cycle.administrationDate),
      arriveDate: formatDate(cycle.arriveDate),
      billVoided: cycle.billVoided,
      billVoidedDate: formatDate(cycle.billVoidedDate),
      dosageUnit: cycle.dosageUnit,
      drugDosage: cycle.drugDosage,
      estimatedDelivery: formatDate(cycle.estimatedDelivery),
      isFreeDrug: cycle.isFreeDrug,
      lotNumberDispensed: cycle.lotNumberDispensed,
      lotNumberReceived: cycle.lotNumberReceived,
      orderDate: formatDate(cycle.orderDate),
      quantity: cycle.quantity,
      refills: cycle.refills,
      status: cycle.status,
      totalAmount: cycle.totalAmount,
      unitCost: cycle.unitCost
    };
  };

  const updateFormWithNewCycle = (newCycle) => {
    const currentFormValues = form.getFieldsValue();
    const lastItemKey = tableDate.length.toString();
    const newValues = prepareCycleDataForForm(newCycle);

    form.setFieldsValue({ ...currentFormValues, [lastItemKey]: newValues });
  };

  const handleAdd = () => {
    setIsAddingPatientCycle(true);
    const lastCycle = allPatientCycles[allPatientCycles.length - 1];
    const newCycle = getNewCycle(patient, lastCycle);

    if (editingAll) {
      updateFormWithNewCycle(newCycle);
    }

    addPatientCycleAct(newCycle);
  };

  const adjustScroll = (errorFields) => {
    const failedFieldName = errorFields.errorFields[0]?.name;
    const failedFieldId = failedFieldName?.length > 1 ? failedFieldName[1] : failedFieldName[0];

    const vp_lastFixedItemCords = document.querySelector(".ant-table-cell-fix-left-last").getBoundingClientRect();
    const vp_failedFieldHeaderCell = document.querySelector(`#${failedFieldId}_headerCell`);
    const vp_failedFieldThCords = vp_failedFieldHeaderCell.getBoundingClientRect();

    const fixedStart = vp_lastFixedItemCords.x;
    const fixedEnd = fixedStart + vp_lastFixedItemCords.width;
    const failedStart = vp_failedFieldThCords.x;
    const scrollLeftDistance = document.querySelector("#cycles .ant-table-body").scrollLeft;
    const gapDistance = fixedEnd - failedStart;

    if (vp_failedFieldHeaderCell.classList.contains("ant-table-cell-fix-left")) {
      return;
    } else {
      document.querySelector("#cycles .ant-table-body").scrollTo({
        left: scrollLeftDistance - gapDistance - 10
      });
    }
  };

  const getRowClassName = (record) => {
    if (record.id === (patient.currentOrder?.id || patient.pharmacyPatientCurrentOrder)) {
      return "highlight-row";
    }
    return "";
  };

  const updatePharmacyPatient = (pharmacyPatient, value) => {
    return {
      ...pharmacyPatient,
      refills: value.refills,
      orderDate: value.orderDate.toISOString(),
      administrationDate: value.administrationDate.toISOString(),
      billVoided: value.billVoided,
      isFreeDrug: value.isFreeDrug,
      drugDosage: value.drugDosage,
      dosageUnit: value.dosageUnit,
      quantity: value.quantity,
      unitCost: value.unitCost,
      totalAmount: value.totalAmount,
      status: value.status,
      billVoidedDate: value.billVoidedDate?.toISOString(),
      accountNumber: value.accountNumber,
      lotNumberDispensed: value.lotNumberDispensed,
      lotNumberReceived: value.lotNumberReceived
    };
  };

  const save = async (orderIndex) => {
    try {
      const values = await form.validateFields();
      const cycle = cycles[orderIndex];
      setIsLoadingUpdateCycleInfo(true);

      let pharmacyPatient = {
        id: cycle.pharmacyPatient,
        pharmacyPatient: cycle.pharmacyPatient
      };

      if (cycle.id === (patient.pharmacyPatientCurrentOrder || patient.currentOrder.id)) {
        pharmacyPatient = updatePharmacyPatient(pharmacyPatient, values);
      }

      const newCycles = cycles.map((cycle, index) => {
        const billVoided = index === orderIndex ? values.billVoided : cycle.billVoided;
        const administrationDate =
          index === orderIndex ? values.administrationDate.toISOString() : cycle.administrationDate;
        const orderDate = index === orderIndex ? values.orderDate.toISOString() : cycle.orderDate;
        const estimatedDelivery =
          index === orderIndex
            ? values.estimatedDelivery
              ? values.estimatedDelivery.toISOString()
              : null
            : cycle.estimatedDelivery;
        const arriveDate =
          index === orderIndex ? (values.arriveDate ? values.arriveDate.toISOString() : null) : cycle.arriveDate;
        const isFreeDrug = index === orderIndex ? values.isFreeDrug : cycle.isFreeDrug;
        const drugDosage = index === orderIndex ? values.drugDosage : cycle.drugDosage;
        const dosageUnit = index === orderIndex ? values.dosageUnit : cycle.dosageUnit;
        const quantity = index === orderIndex ? values.quantity : cycle.quantity;
        const unitCost = index === orderIndex ? values.unitCost : cycle.unitCost;
        const totalAmount = index === orderIndex ? values.totalAmount : cycle.totalAmount;
        const billVoidedDate =
          index === orderIndex
            ? values.billVoidedDate
              ? values.billVoidedDate.toISOString()
              : null
            : cycle.billVoidedDate;
        const accountNumber = index === orderIndex ? values.accountNumber : cycle.accountNumber;
        const lotNumberDispensed = index === orderIndex ? values.lotNumberDispensed : cycle.lotNumberDispensed;
        const lotNumberReceived = index === orderIndex ? values.lotNumberReceived : cycle.lotNumberReceived;

        let status;
        if (index === orderIndex) {
          status =
            values.isFreeDrug !== cycle.isFreeDrug && cycle.status === values.status
              ? Statuses[cycle.status].getFirstStep(values.isFreeDrug)
              : values.status;
        } else {
          status = cycle.status;
        }
        let refills = index === orderIndex ? values.refills : cycle.refills;

        if (values.refills && index > orderIndex) {
          const newRefills = values.refills - (index - orderIndex);
          refills = newRefills >= 0 ? newRefills : null;
        }

        return {
          ...cycle,
          refills,
          administrationDate,
          orderDate,
          billVoided,
          estimatedDelivery: estimatedDelivery || "",
          arriveDate: arriveDate || "",
          isFreeDrug,
          drugDosage,
          dosageUnit,
          quantity,
          unitCost,
          totalAmount,
          status,
          billVoidedDate: billVoidedDate || "",
          accountNumber,
          lotNumberDispensed,
          lotNumberReceived
        };
      });
      await bulkChangeAct(pharmacyPatient, newCycles, true);
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
      adjustScroll(errInfo);
    }
  };

  const onChangeCurrCycle = (record) => {
    const currentOrder = record;
    const pharmacyPatient = { id: record.pharmacyPatient };
    changeCurrentOrderAct(pharmacyPatient, currentOrder);
  };

  const deleteLastCycle = (cycleId) => {
    deletePatientCycleAct(cycleId, patient);
  };

  const handleAdminDateFilter = (fromDate, toDate) => {
    dispatch(ACTIONS.setCycleAdminDateFilter({ fromDate, toDate }));
    dispatch(ACTIONS.refreshCyclesAction(patient.id));
  };

  const getAdminDateFilterOptions = () => [
    { value: THIS_YEAR, id: ADMIN_DATE_FILTER_THIS_YEAR_ID },
    { value: NEXT_12_MONTHS, id: ADMIN_DATE_FILTER_NEXT_12_MONTHS_ID },
    { value: NEXT_CALENDAR_YEAR, id: ADMIN_DATE_FILTER_NEXT_CALENDAR_YEAR_ID },
    { value: PREVIOUS_YEAR, id: ADMIN_DATE_FILTER_PREVIOUS_YEAR_ID },
    { value: ALL, id: ADMIN_DATE_FILTER_ALL_CYCLES_ID }
  ];

  const columns = [
    {
      dataIndex: "currentCycle",
      render: (text, record) =>
        record.id === (patient.pharmacyPatientCurrentOrder || patient.currentOrder.id) ? (
          <div data-tm-id="act-cycle-slector-btn">
            <EllipseIcon />
          </div>
        ) : (
          <div data-tm-id="act-cycle-slector-btn">
            <EllipseEmptyIcon />
          </div>
        ),
      editable: true,
      width: 50,
      changeCurrentCycle: true,
      fixed: "left"
    },
    {
      dataIndex: "operation",
      width: 40,
      fixed: "left",
      render: (_, record) => {
        if (editingAll || editingKey === record.key) return;

        return (
          <EditButtonStyle
            data-tm-id={`act-edit-btn-${record.key}`}
            id={`act-edit-btn-${record.key}`}
            disabled={editingKey !== "" || !isActive}
            onClick={() => edit(record)}
            type="text"
            icon={<EditPencilIcon />}
          />
        );
      }
    },
    {
      dataIndex: "deleteCycle",
      render: (text, record) => {
        const isCurrentCycle = record.id === (patient.pharmacyPatientCurrentOrder || patient.currentOrder.id);
        const isInEditMode = editingAll || editingKey !== "";

        if (isCurrentCycle || isInEditMode) {
          return null;
        }

        return (
          <Button
            data-tm-id="act-delete-btn"
            id="act-delete-btn"
            type="text"
            disabled={isFetchingCycleDelete || isFetchingCycles}
            loading={isFetchingCycleDelete}
            style={{ padding: "4px 4px" }}
          >
            <DeleteOutlined
              onClick={() => {
                setSelectedRow(record);
                setShowDeleteModal(true);
              }}
            />
          </Button>
        );
      },
      width: 35,
      fixed: "left"
    },
    {
      title: () => {
        return <div data-tm-id="act-cycle">{t("patient.cycles.cycle_title")}</div>;
      },
      dataIndex: "cycle",
      width: 90,
      defaultSortOrder: ASCEND,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b) => a.orderIndex - b.orderIndex,
      render: (text, record) => `${record.orderIndex + 1} / ${allPatientCycles.length}`,
      fixed: "left"
    },
    {
      title: () => {
        return <div data-tm-id="act-type">{t("patient.cycles.type_title")}</div>;
      },
      width: isDataContainsReplacemnetDrug() || edited || editingAll ? 115 : 50,
      dataIndex: "isFreeDrug",
      editable: true,
      render: (text, record) => (
        <div>{record.isFreeDrug ? OrderType.free.value(t) : OrderType.replacement.value(t)}</div>
      ),
      fixed: "left"
    },
    {
      title: () => {
        return <div data-tm-id="act-status">{t("patient.cycles.status_title")}</div>;
      },
      dataIndex: "status",
      width: edited || editingAll ? 160 : 130,
      editable: true,
      render: (text, record) =>
        Object.values(Statuses)
          .find((status) => status.key === record.status)
          .title(t),
      fixed: "left"
    },
    {
      title: () => {
        return <div data-tm-id="act-order">{t("patient.cycles.order_title")}</div>;
      },
      dataIndex: "orderDate",
      width: edited ? 130 : 120,
      editable: true,
      render: (text, record) => (record.orderDate ? moment(record.orderDate).format(DATE_FORMAT_2) : INVALID_DATE),
      fixed: "left"
    },
    {
      title: () => {
        return <div data-tm-id="act-admin">{t("patient.cycles.admin_title")}</div>;
      },
      dataIndex: "administrationDate",
      width: edited || editingAll ? 150 : 110,
      editable: true,
      render: (text, record) =>
        record.administrationDate ? moment(record.administrationDate).format(DATE_FORMAT_2) : INVALID_DATE,
      fixed: "left"
    },
    {
      title: () => {
        return <div data-tm-id="act-estimated-delivery">{t("patient.cycles.estimated_delivery_title")}</div>;
      },
      dataIndex: "estimatedDelivery",
      width: edited ? 135 : 145,
      editable: true,
      render: (text, record) => record.estimatedDelivery && record.estimatedDelivery.format(DATE_FORMAT_2)
    },
    {
      title: () => {
        return <div data-tm-id="act-received-date">{t("patient.cycles.arrive_date_title")}</div>;
      },
      dataIndex: "arriveDate",
      width: edited ? 135 : 115,
      editable: true,
      render: (text, record) => record.arriveDate && record.arriveDate.format(DATE_FORMAT_2)
    },
    {
      title: () => {
        return <div data-tm-id="act-lot-number-received">{t("patient.cycles.lot_number_received")}</div>;
      },
      dataIndex: "lotNumberReceived",
      width: 160,
      editable: true,
      render: (_, record) => <AutoEllipsisTooltipColumnValue>{record.lotNumberReceived}</AutoEllipsisTooltipColumnValue>
    },
    {
      title: () => {
        return <div data-tm-id="act-lot-number-dispensed">{t("patient.cycles.lot_number_dispensed")}</div>;
      },
      width: 170,
      dataIndex: "lotNumberDispensed",
      editable: true,
      render: (_, record) => (
        <AutoEllipsisTooltipColumnValue>{record.lotNumberDispensed}</AutoEllipsisTooltipColumnValue>
      )
    },
    {
      title: () => {
        return <div data-tm-id="act-dosage">{t("patient.cycles.dosage_title")}</div>;
      },
      dataIndex: "drugDosage",
      width: 80,
      editable: true,
      render: (_, record) => <AutoEllipsisTooltipColumnValue>{record.drugDosage}</AutoEllipsisTooltipColumnValue>
    },
    {
      title: () => {
        return <div data-tm-id="act-unit">{t("patient.cycles.unit_title")}</div>;
      },
      dataIndex: "dosageUnit",
      width: 100,
      editable: true
    },
    {
      title: () => {
        return <div data-tm-id="act-quantity">{t("patient.cycles.quantity_title_abbreviation")}</div>;
      },
      dataIndex: "quantity",
      editable: true,
      width: 70,
      render: (_, record) => (
        <AutoEllipsisTooltipColumnValue>{record.quantity ? record.quantity : ""}</AutoEllipsisTooltipColumnValue>
      )
    },
    {
      title: () => {
        return <div data-tm-id="act-unit-cost">{t("patient.cycles.unit_cost_title")}</div>;
      },
      dataIndex: "unitCost",
      width: 100,
      editable: true,
      render: (_, record) => (
        <AutoEllipsisTooltipColumnValue>{record.unitCost ? `$${record.unitCost}` : ""}</AutoEllipsisTooltipColumnValue>
      )
    },
    {
      title: () => {
        return <div data-tm-id="act-total-amount">{t("patient.cycles.total_amount_title")}</div>;
      },
      dataIndex: "totalAmount",
      width: 100,
      editable: true,
      render: (_, record) => (
        <AutoEllipsisTooltipColumnValue>
          {record.totalAmount ? `$${record.totalAmount}` : ""}
        </AutoEllipsisTooltipColumnValue>
      )
    },
    {
      title: () => {
        return <div data-tm-id="act-account-number">{t("patient.cycles.account_number")}</div>;
      },
      dataIndex: "accountNumber",
      width: 140,
      editable: true,
      render: (_, record) => <AutoEllipsisTooltipColumnValue>{record.accountNumber}</AutoEllipsisTooltipColumnValue>
    },
    {
      title: () => {
        return <div data-tm-id="act-bill-voided">{t("patient.cycles.bill_voided_title")}</div>;
      },
      dataIndex: "billVoided",
      width: 90,
      editable: true,
      render: (text, record) => (record.billVoided ? t("form.bill_voided.yes_text") : t("form.bill_voided.no_text"))
    },
    {
      title: () => {
        return <div data-tm-id="act-bill-voided-date">{t("patient.cycles.bill_voided_date")}</div>;
      },
      dataIndex: "billVoidedDate",
      width: edited ? 135 : 125,
      editable: true,
      render: (text, record) => (record.billVoidedDate ? moment(record.billVoidedDate).format(DATE_FORMAT_2) : "")
    },
    {
      title: () => {
        return <div data-tm-id="act-refills">{t("patient.cycles.refills_title")}</div>;
      },
      dataIndex: "refills",
      width: 110,
      editable: true,
      render: (_, record) => <AutoEllipsisTooltipColumnValue>{record.refills}</AutoEllipsisTooltipColumnValue>
    }
  ];

  const onCycleStatusChange = (newStatus, { arriveDate }) => {
    if (arriveDate || isCycleStatusManuallyChanged) return;

    if (newStatus === Keys.in_pharmacy || newStatus === Keys.in_pharmacy_free) {
      autoPopulateArriveDate();
    } else removeAutoPopulateArriveDate();
  };

  const autoPopulateArriveDate = () => {
    form.setFieldsValue({ arriveDate: moment() });
    setCycleStatusManuallyChanged(false);
  };

  const removeAutoPopulateArriveDate = () => {
    form.setFieldsValue({ arriveDate: null });
    setCycleStatusManuallyChanged(false);
  };

  const onArriveDateChange = () => {
    setCycleStatusManuallyChanged(true);
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    const getInputNode = (col, record) => {
      const recordInfo = editingAll ? { key: record.key, dataIndex: col.dataIndex } : null;

      switch (col.dataIndex) {
        case "refills":
          return (
            <NumericalInput
              name="refills"
              placeholder={t("form.refills.placeholder")}
              min={0}
              max={REFILL_MAX}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "isFreeDrug":
          return (
            <OrderTypeFormItem
              defaultValue={record.isFreeDrug ? OrderType.free.value(t) : OrderType.replacement.value(t)}
              required={true}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "orderDate":
          return (
            <OrderDateFormItem
              defaultValue={record.orderDate}
              required={true}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "administrationDate":
          return (
            <AdminDateFormItem
              defaultValue={record.administrationDate}
              required={true}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "drugDosage":
          return <DosageFormItem style={{ marginBottom: 0 }} recordInfo={recordInfo} />;
        case "dosageUnit":
          return <UnitFormItem style={{ marginBottom: 0 }} recordInfo={recordInfo} />;
        case "quantity":
          return (
            <NumericalInput
              name="quantity"
              placeholder={t("form.quantity.placeholder")}
              rules={[{ required: true, message: t("form.quantity.error_required") }]}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "unitCost":
          return (
            <NumericalInput
              name="unitCost"
              placeholder={t("form.unitCost.placeholder")}
              prefix="$"
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "totalAmount":
          return (
            <NumericalInput
              name="totalAmount"
              placeholder={t("form.totalAmount.placeholder")}
              prefix="$"
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "estimatedDelivery":
          return <EstimatedDateFormItem style={{ marginBottom: 0 }} recordInfo={recordInfo} />;
        case "arriveDate":
          return (
            <ArrivedDateFormItem
              style={{ marginBottom: 0 }}
              id="receivedDate"
              onChange={onArriveDateChange}
              recordInfo={recordInfo}
            />
          );
        case "billVoided":
          return (
            <BillVoidedFormItem
              defaultValue={record.billVoided ? t("form.bill_voided.yes_text") : t("form.bill_voided.no_text")}
              required={true}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "status":
          return (
            <StatusFormItem
              defaultValue={record.status}
              required={true}
              isFreeDrug={record.isFreeDrug}
              style={{ display: "inline-block", marginBottom: 0 }}
              onSelect={(newStatus) => onCycleStatusChange(newStatus, record)}
              recordInfo={recordInfo}
            />
          );
        case "cycle":
          return <Text>{`${record.orderIndex + 1} / ${cycles.length}`}</Text>;
        case "currentCycle":
          return record.id === (patient.pharmacyPatientCurrentOrder || patient.currentOrder.id) ? (
            <EllipseIcon />
          ) : (
            <EllipseEmptyIcon />
          );
        case "lotNumberReceived":
          return (
            <LotNumberFormItem
              name="lotNumberReceived"
              dataTMId="cct-lot-number-received"
              defaultValue={record.lotNumberReceived}
              placeholder={t("form.lot_number_received.placeholder")}
              minNumberOfDigits={0}
              maxNumberOfDigits={LOT_NUMBER_MAX}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "accountNumber":
          return (
            <LotNumberFormItem
              name="accountNumber"
              dataTMId="cct-account-number"
              defaultValue={record.accountNumber}
              placeholder={t("form.account_number.placeholder")}
              minNumberOfDigits={0}
              maxNumberOfDigits={LOT_NUMBER_MAX}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "lotNumberDispensed":
          return (
            <LotNumberFormItem
              name="lotNumberDispensed"
              dataTMId="cct-lot-number-dispensed"
              placeholder={t("form.lot_number_dispensed.placeholder")}
              minNumberOfDigits={0}
              maxNumberOfDigits={LOT_NUMBER_MAX}
              defaultValue={record.lotNumberDispensed}
              style={{ marginBottom: 0 }}
              recordInfo={recordInfo}
            />
          );
        case "billVoidedDate":
          return (
            <BillVoidedDateFormItem
              required={true}
              style={{ marginBottom: 0 }}
              form={form}
              onlyDate={true}
              dataTMId="cct-bill-voided"
              defaultDateValue={record.billVoidedDate}
              recordInfo={recordInfo}
            />
          );
        default:
          return col;
      }
    };

    return {
      ...col,
      onHeaderCell: (column) => ({
        id: `${column.dataIndex}_headerCell`
      }),
      onCell: (record) => ({
        record,
        inputNode: getInputNode(col, record),
        dataIndex: col.dataIndex,
        title: col.title,
        editing: editingAll || isEditing(record),
        changeCurrentCycle: col.changeCurrentCycle
      })
    };
  });

  const handleClickEditAll = () => {
    setKeepEditingCycleAct(true);
    setEditingAll(true);
    updateFormData();
  };

  const saveAll = async () => {
    try {
      const values = await form.validateFields();

      setIsLoadingUpdateCycleInfo(true);

      let pharmacyPatient = {
        id: cycles[0]?.pharmacyPatient,
        pharmacyPatient: cycles[0]?.pharmacyPatient
      };

      const updatedCycles = cycles.map((cycle, index) => {
        const value = values[index];

        if (value) {
          if (cycle.id === (patient.pharmacyPatientCurrentOrder || patient.currentOrder.id)) {
            pharmacyPatient = updatePharmacyPatient(pharmacyPatient, value);
          }

          const status =
            value.isFreeDrug !== cycle.isFreeDrug && cycle.status === value.status
              ? Statuses[cycle.status].getFirstStep(value.isFreeDrug)
              : value.status;

          return {
            ...cycle,
            refills: value.refills,
            administrationDate: value.administrationDate.toISOString() || "",
            orderDate: value.orderDate.toISOString() || "",
            billVoided: value.billVoided,
            estimatedDelivery: value.estimatedDelivery?.toISOString() || "",
            arriveDate: value.arriveDate?.toISOString() || "",
            isFreeDrug: value.isFreeDrug,
            drugDosage: value.drugDosage,
            dosageUnit: value.dosageUnit,
            quantity: value.quantity,
            unitCost: value.unitCost,
            totalAmount: value.totalAmount,
            status,
            billVoidedDate: value.billVoidedDate?.toISOString() || "",
            accountNumber: value.accountNumber,
            lotNumberDispensed: value.lotNumberDispensed,
            lotNumberReceived: value.lotNumberReceived
          };
        }
        return cycle;
      });

      await bulkChangeAct(pharmacyPatient, updatedCycles, true);
    } catch (errInfo) {
      adjustScroll(errInfo);
    }
  };

  return (
    <ReachableContext.Provider value={null}>
      <Space style={{ marginBottom: 16, width: "100%", display: "flex", justifyContent: "space-between" }}>
        <Space>
          <Button
            disabled={!isActive || isFetchingCycleDelete}
            loading={isAddingPatientCycle || isFetchingChangeCycle || isFetchingPharmacyPatients}
            onClick={handleAdd}
            type="primary"
            data-tm-id="act-add-cycle-btn"
            id="act-add-cycle-btn"
          >
            {t("patient.cycles.add_cycle_button")}
          </Button>
          <Select
            id="act-set-all-cycles"
            disabled={!isActive}
            placeholder={t("patient.cycles.set_all_placeholder")}
            style={{ width: 230 }}
            onSelect={setTypeAll}
            data-tm-id="act-set-all-cycles"
          >
            <Option id="act-set-all-cycles-free" value={true}>
              {t("patient.cycles.set_all_free")}
            </Option>
            <Option id="act-set-all-cycles-replacement" value={false}>
              {t("patient.cycles.set_all_replacement")}
            </Option>
          </Select>

          <Space direction="horizontal" size={12} style={{ marginLeft: "20px", width: "100%" }}>
            <StyledAdminDateLabel>{t("patient.cycles.admin_date")}:</StyledAdminDateLabel>

            <StyledDateRangeFilter>
              <DateRangeFilter
                dataTMId={ADMIN_DATE_FILTER_ID}
                timeRange={adminDateFilter}
                onSelect={handleAdminDateFilter}
                options={getAdminDateFilterOptions()}
                showSearch={false}
                defaultValue={THIS_YEAR}
                customRangeId={ADMIN_DATE_FILTER_CUSTOM_ID}
                disabled={isEditingActive}
                style={{
                  border: `1px solid ${GRAY_200}`,
                  borderBottomColor: GRAY_200,
                  minWidth: "160px",
                  width: "100%",
                  color: isEditingActive ? GRAY_400 : BLACK,
                  backgroundColor: isEditingActive ? GRAY_50 : "transparent"
                }}
                placeholder={t("this_year")}
                onClear={() => {
                  handleAdminDateFilter(THIS_YEAR, null);
                }}
              />
            </StyledDateRangeFilter>
          </Space>
        </Space>
        {isEditing(recordForEditing) || editingAll ? (
          <Space>
            <Popconfirm title={t("sure_to_cancel")} onConfirm={cancel}>
              <Button id={editingAll ? "act-edit-all-cycles-cancel" : "act-edit-single-cycle-cancel"}>
                {t("cancel")}
              </Button>
            </Popconfirm>
            <Button
              id={editingAll ? "act-edit-all-cycles-submit" : "act-edit-single-cycle-submit"}
              onClick={() => (editingAll ? saveAll() : save(recordForEditing.key))}
              type="primary"
              loading={isLoadingUpdateCycleInfo && !isModalOpen}
            >
              {t("save")}
            </Button>
          </Space>
        ) : (
          <Button id="edit_all_cycles_btn" onClick={handleClickEditAll}>
            {t("patient.cycles.edit_all_cycles")}
          </Button>
        )}
      </Space>

      <SingleCycleForm form={form} component={false} isClickedEditingAll={editingAll}>
        <StyledTable
          id="cycles"
          components={{
            body: {
              cell: EditableCell
            }
          }}
          size={"small"}
          rowClassName={getRowClassName}
          enableFlash={isFlashEnabled}
          dataSource={tableDate}
          columns={mergedColumns}
          pagination={false}
          loading={isFetchingCycleDelete || isFetchingChangeCycle || isFetchingCycles}
          scroll={{ x: 2000, y: tableDate?.length === 0 ? undefined : 380 }}
          ref={cycleTableRef}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <StyledEmptyDescription>
                    <Text style={{ fontWeight: fontWeights.bold }}>{t("patient.cycles.empty_text_title")}</Text>
                    <Text>{t("patient.cycles.empty_text_description")}</Text>
                  </StyledEmptyDescription>
                }
              />
            )
          }}
        />
      </SingleCycleForm>
      <ModalComponent
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        okText={t("ok_text")}
        onOk={async () => {
          deleteLastCycle(selectedRow.id);
          setShowDeleteModal(false);
        }}
      >
        <Space direction="vertical">
          <DeleteIcon style={{ width: "48px" }} />
          <Text className="title">{t("patient.cycles.confirm_delete_title")}</Text>
          <Text className="content">
            {isCycleWithNoteOrAttachments(selectedRow)
              ? t("patient.cycles.confirm_delete_content_with_notes_attach")
              : t("patient.cycles.confirm_delete_content")}
          </Text>
        </Space>
      </ModalComponent>
      {contextHolder}
    </ReachableContext.Provider>
  );
};

const mapStateToProps = createStructuredSelector({
  isFetchingChangeCycle: selectIsFetching([ApiNames.PatientOrders]),
  isFetchingCycleDelete: selectIsFetching([ApiNames.CycleDelete])
});

const mapDispatchToProps = (dispatch) => ({
  changeCurrentOrderAct: (pharmacyPatient, currentOrder) =>
    dispatch(ACTIONS.changeCurrentOrderAction(pharmacyPatient, currentOrder)),
  bulkChangeAct: (pharmacyPatient, orders, stopEditingCycleMode) =>
    dispatch(ACTIONS.bulkChangeAction(pharmacyPatient, orders, stopEditingCycleMode)),
  addPatientCycleAct: (newCycle) => dispatch(ACTIONS.addPatientCycleAction(newCycle)),
  deletePatientCycleAct: (id, pharmacyPatient) => dispatch(ACTIONS.deletePatientCycleAction(id, pharmacyPatient))
});

const PatientCyclesTab = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PatientCyclesTabComponent));

export { PatientCyclesTab };
